import { createSelector } from '@reduxjs/toolkit'
import { Map } from 'immutable'
import { getColorSchemeForTimeOfDay } from '../../components/MemberHomeFirstTouch/utils'

/**
 * True when the measured viewport is below `breakpoint`. Width `0` is the SSR
 * placeholder — treat it as unknown, not mobile, so chrome (e.g. primary nav)
 * does not switch to an empty MobileNav between bootstrapComplete and initViewport.
 */
export function selectIsBreakpoint (breakpoint) {
  return (state) => {
    const hydrationComplete = state.app.get('bootstrapComplete')
    const viewport = state.app.getIn(['viewport', 'width'], 0)
    return hydrationComplete && viewport > 0 && viewport < breakpoint
  }
}

/**
 * @param {ReduxState} state redux state
 * @returns {Boolean}
 */
export const selectIsTouchable = (state) => state.app.getIn(['viewport', 'touchable'], false)

/**
 * @param {ReduxState} state redux state
 * @returns {Map}
 */
export const selectApp = (state) => state.app || Map()

/**
 * @param {ReduxState} state redux state
 * @returns {Number}
 */
export const selectAppViewportWidth = (state) => selectApp(state).getIn(['viewport', 'width'], 0)

/**
 * @param {ReduxState} state redux state
 * @returns {Number}
 */
export const selectAppViewportHeight = (state) => selectApp(state).getIn(['viewport', 'height'], 0)

const emptyMemberHomeFirstTouch = Map({ visible: false, variation: null })

const emptyHeaderContext = Map({ visible: false, variation: null, colorScheme: null })

/**
 * @param {ReduxState} state
 * @returns {import('immutable').Map}
 */
export const selectMemberHomeFirstTouch = (state) => {
  const memberHomeFirstTouch = selectApp(state).get('memberHomeFirstTouch')
  return Map.isMap(memberHomeFirstTouch)
    ? memberHomeFirstTouch
    : emptyMemberHomeFirstTouch
}

/**
 * Header styling for the Member Home First Touch overlay: visibility, CMS
 * variation slug, and light/dark color scheme. Returned as an Immutable Map so
 * connectRedux's shallow comparison stays stable across renders.
 * @param {ReduxState} state
 * @returns {import('immutable').Map}
 */
export const selectMemberHomeFirstTouchHeaderContext = createSelector(
  [selectMemberHomeFirstTouch],
  (memberHomeFirstTouch) => {
    const visible = !!memberHomeFirstTouch.get('visible')
    const variation = memberHomeFirstTouch.get('variation')
    const timeOfDay = memberHomeFirstTouch.get('timeOfDay')
    const resolvedVariation = visible && variation != null ? variation : null
    const colorScheme = visible && timeOfDay != null && timeOfDay !== ''
      ? getColorSchemeForTimeOfDay(timeOfDay)
      : null
    if (!visible && resolvedVariation == null && colorScheme == null) {
      return emptyHeaderContext
    }
    return Map({ visible, variation: resolvedVariation, colorScheme })
  },
)

/**
 * Build/runtime app version string. Sourced from the app reducer (set
 * once at boot from package.json or the deploy manifest). Used as the
 * Layer 1 PostHog super-property `app_version`.
 * @param {ReduxState} state redux state
 * @returns {string | null}
 */
export const selectAppVersion = (state) => state.app.get('version') || null
